import { defineStore } from "pinia";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  limit,
} from "firebase/firestore"; // Import necessary Firebase functions
import {} from "firebase/storage";
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
} from "firebase/storage";
import { shallowRef } from "vue";
import { useCountyStore } from "./countyStore";

async function fetchJsonData(filename) {
  const storage = getStorage();
  const fileRef = storageRef(storage, filename);
  try {
    const downloadURL = await getDownloadURL(fileRef);

    // 5. Load the GeoJSON data from the URL
    const response = await fetch(downloadURL);
    const jsonData = await response.json();
    return jsonData;
  } catch (error) {
    console.log(`Error fetching ${fileRef}:`, error);
    throw error;
  }
}

/**
 * Generates a link to a contact's details in Aplos.
 *
 * @param {string} id - The Aplos ID of the contact.
 * @returns {string} The Aplos contact URL.
 */
export function linkToAplosContact(id) {
  return `https://app.aplos.com/aws/client-apps/contacts/${id}/details`;
}

/**
 * Clamps a value within a specified range.
 *
 * @param {number} val - The value to clamp.
 * @param {number} min - The minimum value of the range.
 * @param {number} max - The maximum value of the range.
 * @returns {number} The clamped value.
 */
export function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

/**
 * Creates a function that clamps a metric value within a specified range, using a potentially asynchronous function to retrieve the metric value.
 *
 * @param {object} metric - An object defining the metric, including its asynchronous function, minimum, and maximum values.  Must contain `function`, `min`, and `max` properties.
 * @returns {function(string): Promise<number>} An asynchronous function that takes a precinct identifier and returns a clamped metric value.
 * @throws {Error} If the metric object is missing required properties.
 */
export function metric_fun(metric) {
  if (
    !metric ||
    !metric.function ||
    typeof metric.min === "undefined" ||
    typeof metric.max === "undefined"
  ) {
    throw new Error(
      "Invalid metric object.  Must include 'function', 'min', and 'max' properties."
    );
  }
  if (metric.unit == "Category") {
    return async p => {
      const metricFunctionResult = await metric.function(p);
      return metricFunctionResult;
    };
  } else {
    return async p => {
      const val = await metric.function(p);
      if (val === null) return null;
      return clamp(val, metric.min, metric.max);
    };
  }
}

/**
 * Pinia store for managing application data.
 */
export const useDataStore = defineStore("data", {
  /**
   * Defines the state of the data store.
   * @returns {object} An object containing the store's state.
   */
  state: () => {
    return {
      // county: "",
      /* These are `shallowRef`  because they contain a lot of data and we don't need Vue searching for any little change. */
      metrics: shallowRef([]),
      precinctDataSets: shallowRef({ Greene: {} }),
      countyDemVoterGeocodes: null,
      countyVoterGeocodes: shallowRef({}),
      contactGeocodes: shallowRef({}),
      countyContacts: shallowRef({}),
      countyContactsById: shallowRef({}),
      firebaseContactsByUserId: shallowRef({}),
      precinctCaptains: null,
      precinctContacts: null,
      precinctContactsBySOSID: shallowRef({}),
      precinctDemVoters: shallowRef({}),
      precinctVoters: shallowRef({}),
      precinctVoterStatistics: null,
      _pendingRequests: new Map(), // Add this for request deduplication
    };
  },

  getters: {
    /**
     * Retrieves the current county from the county store.
     * @returns {string} The name of the current county.
     */
    county: () => useCountyStore().county,
  },

  /**
   * Defines the actions of the data store.
   */
  actions: {
    // Add helper method for request deduplication
    _deduplicateRequest(key, requestFn) {
      if (this._pendingRequests.has(key)) {
        return this._pendingRequests.get(key);
      }

      const promise = requestFn().finally(() => {
        this._pendingRequests.delete(key);
      });

      this._pendingRequests.set(key, promise);
      return promise;
    },
    /**
     * Sets the current county and fetches relevant data.
     * @async
     * @param {string} county - The name of the county.
     */
    async setCounty(county) {
      if (useCountyStore().county !== county) {
        // Clear existing data
        this.countyContacts = {};
        this.countyContactsById = {};
        this.precinctDataSets = {};
        this.precinctCaptains = null;
        this.precinctContacts = null;
        this.precinctDemVoters = {};
        this.countyDemVoterGeocodes = null;
        this.precinctVoterStatistics = null;
        useCountyStore().county = county;
      }

      if (!(county in this.precinctDataSets)) {
        this.precinctDataSets[county] = {};
      }

      // Load critical data concurrently
      const [metricsResult, contactsResult, voterMetricsResult] =
        await Promise.allSettled([
          this.fetchMetricsFromFirestore(county),
          this.fetchPrecinctContacts(),
          this.fetchVoterMetrics(),
        ]);

      // Set metrics from both sources
      const voterMetrics =
        voterMetricsResult.status === "fulfilled"
          ? voterMetricsResult.value
          : [];
      const firestoreMetrics =
        metricsResult.status === "fulfilled" ? this.metrics : [];
      this.metrics = [...voterMetrics, ...firestoreMetrics];

      // Log any errors but don't fail the entire operation
      if (metricsResult.status === "rejected") {
        console.error(
          "Failed to load Firestore metrics:",
          metricsResult.reason
        );
      }
      if (contactsResult.status === "rejected") {
        console.error("Failed to load contacts:", contactsResult.reason);
      }
      if (voterMetricsResult.status === "rejected") {
        console.error(
          "Failed to load voter metrics:",
          voterMetricsResult.reason
        );
      }
    },

    /**
     * Fetches metric data from Firestore.
     * @async
     * @param {string} county - The name of the county.
     */
    async fetchMetricsFromFirestore(county) {
      const db = getFirestore(); // Get Firestore instance
      const metricsCollection = collection(db, "mapDataMetrics"); // Reference to the collection
      // Create a query to filter by county
      const q = query(metricsCollection, where("county", "==", county));

      const querySnapshot = await getDocs(q);
      this.metrics = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        function: async p => {
          try {
            const pa = await this.fetchPrecinctDataSet(doc.id);
            // Scale by 100 for percents.
            if (doc.data()["unit"] == "%") return pa[p] * 100;
            else {
              const pap = pa[p];
              return pap;
            }
          } catch (e) {
            console.error(`retrieving ${p}: ${e}`);
            return -1;
          }
        },
        categories: async () => {
          try {
            const pa = await this.fetchPrecinctDataSet(doc.id);
            return [...new Set(Object.values(pa))].sort();
          } catch (e) {
            return -1;
          }
        },
      }));
    },

    /**
     * Fetches a precinct dataset.
     * @async
     * @param {string} dataSetName - The name of the dataset.
     * @returns {Promise<object>} A promise that resolves to the dataset.
     */
    // Get a dataset for the county precincts.
    async fetchPrecinctDataSet(dataSetName) {
      const countyDataSets = this.precinctDataSets["Greene"];
      if (dataSetName in countyDataSets) {
        return await countyDataSets[dataSetName];
      }

      const precinct_data = fetchJsonData(
        `reports/precinct_data_sets/${this.county}/${dataSetName}.json`
      );
      countyDataSets[dataSetName] = precinct_data;
      return await precinct_data;
    },

    /**
     * Fetches county contact data.
     * @async
     * @returns {Promise<object>} A promise that resolves to the county contact data object with names as keys
     */
    //
    async fetchCountyContacts() {
      if (Object.keys(this.countyContacts).length > 0) {
        return this.countyContacts;
      }

      return this._deduplicateRequest(
        `countyContacts_${this.county}`,
        async () => {
          try {
            const contacts = await fetchJsonData(
              `reports/${this.county}_contacts.json`
            );

            // Use reduce for better performance
            this.countyContacts = contacts.reduce((acc, row) => {
              acc[row.name] = row;
              return acc;
            }, {});

            return this.countyContacts;
          } catch (error) {
            console.error("Error fetching County Contact data:", error);
            throw error;
          }
        }
      );
    },

    /**
     * Fetches county contact data.
     * @async
     * @returns {Promise<object>} A promise that resolves to the county contact data object with names as keys
     */
    //
    async fetchCountyContactsById() {
      if (Object.keys(this.countyContactsById).length > 0) {
        return this.countyContactsById;
      }

      return this._deduplicateRequest(
        `countyContactsById_${this.county}`,
        async () => {
          try {
            // Reuse data from fetchCountyContacts if available
            const contacts = await fetchJsonData(
              `reports/${this.county}_contacts.json`
            );

            this.countyContactsById = contacts.reduce((acc, row) => {
              acc[row.contact_id] = row;
              return acc;
            }, {});

            return this.countyContactsById;
          } catch (error) {
            console.error("Error fetching County Contact data:", error);
            throw error;
          }
        }
      );
    },

    /**
     * Searches county contacts by query.
     * @async
     * @param {string} query - The search query.
     * @returns {Promise<Array>} A promise that resolves to an array of matching contacts.
     */
    async searchCountyContacts(query) {
      const contacts = await this.fetchCountyContacts();
      const lowerQuery = query.toLowerCase();

      // Use more efficient search with early termination for large datasets
      const results = [];
      const maxResults = 50; // Limit results for performance

      for (const [contactName, contact] of Object.entries(contacts)) {
        if (results.length >= maxResults) break;

        if (contactName.toLowerCase().includes(lowerQuery)) {
          results.push(contact);
        }
      }

      return results;
    },

    /**
     * Fetches a Firestore contact by firebase_user_id with in-memory caching.
     *
     * @async
     * @param {string} firebaseUserId - The Firebase Auth user id.
     * @returns {Promise<object|null>} A promise that resolves to the contact data (including `id`) or null.
     */
    async fetchFirebaseContactByUserId(firebaseUserId) {
      if (!firebaseUserId) return null;

      if (
        Object.prototype.hasOwnProperty.call(
          this.firebaseContactsByUserId,
          firebaseUserId
        )
      ) {
        return this.firebaseContactsByUserId[firebaseUserId];
      }

      return this._deduplicateRequest(
        `firebaseContact_${firebaseUserId}`,
        async () => {
          const db = getFirestore();
          const contactsCollection = collection(db, "contacts");
          const contactQuery = query(
            contactsCollection,
            where("firebase_user_id", "==", firebaseUserId),
            limit(1)
          );

          const querySnapshot = await getDocs(contactQuery);
          const contact = querySnapshot.empty
            ? null
            : {
                id: querySnapshot.docs[0].id,
                ...querySnapshot.docs[0].data(),
              };

          this.firebaseContactsByUserId[firebaseUserId] = contact;
          return contact;
        }
      );
    },

    /**
     * Fetches precinct captain data.
     * @async
     * @param {string} county - The name of the county.
     * @returns {Promise<object>} A promise that resolves to the precinct captain data.
     */
    async fetchPrecinctCaptains() {
      const county = this.county || "Greene";
      if (this.precinctCaptains === null) {
        this.precinctCaptains = fetchJsonData(
          `reports/${county}_precinct_captains.json`
        );
      }

      const pcs = await this.precinctCaptains;
      const captains = {};
      pcs.forEach(row => {
        captains[row["precinct"]] = row;
      });
      return captains;
    },
    /**
     * Fetches precinct contact data.
     * @async
     * @returns {Promise<object>} A promise that resolves to the precinct contact data.
     */
    async fetchPrecinctContacts() {
      // Immediately assign a promise to precinctContacts if it's null
      if (this.precinctContacts === null) {
        this.precinctContacts = fetchJsonData(
          `reports/${this.county}_contacts_by_precinct.json`
        );
      }
      return await this.precinctContacts;
    },

    /**
     * Fetches geocodes for precinct contacts.
     * @async
     * @returns {Promise<object>} A promise that resolves to the geocodes.
     */
    async fetchPrecinctContactGeocodes() {
      if (Object.keys(this.contactGeocodes).length > 0) {
        return this.contactGeocodes;
      }
      try {
        const geocodes = await fetchJsonData("reports/contact_geocodes.json");
        this.contactGeocodes = geocodes;
        return this.contactGeocodes;
      } catch (error) {
        console.error("Error fetching Contact Geocode data:", error);
      }
    },
    /**
     * Fetches geocodes for Democratic voters in a county.
     * @async
     * @param {string} county - The name of the county.
     * @returns {Promise<object>} A promise that resolves to the geocodes.
     */
    async fetchCountyVoterGeocodes(county, precinct) {
      const key = `${county}_${precinct}`;

      if (!this.countyVoterGeocodes[precinct]) {
        this.countyVoterGeocodes[precinct] = this._deduplicateRequest(
          `voterGeocodes_${key}`,
          () =>
            fetchJsonData(
              `geocodes/${county.toLowerCase()}/${precinct}_geocodes_by_address.json`
            )
        );
      }

      try {
        return await this.countyVoterGeocodes[precinct];
      } catch (error) {
        console.error("Error fetching County Voter Geocodes:", error);
        // Return empty object instead of throwing to prevent cascade failures
        return {};
      }
    },
    /**
     * Fetches geocodes for Democratic voters in a county.
     * @async
     * @param {string} county - The name of the county.
     * @returns {Promise<object>} A promise that resolves to the geocodes.
     */
    async fetchCountyDemVoterGeocodes(county) {
      if (!this.countyDemVoterGeocodes) {
        this.countyDemVoterGeocodes = this._deduplicateRequest(
          `demVoterGeocodes_${county}`,
          () => fetchJsonData(`reports/${county}_voter_geocodes.json`)
        );
      }

      try {
        return await this.countyDemVoterGeocodes;
      } catch (error) {
        console.error("Error fetching County Dem Geocodes:", error);
        return {};
      }
    },
    /**
     * Fetches Democratic voter data for a specific precinct.
     * @async
     * @param {string} county - The name of the county.
     * @param {string} precinct - The precinct identifier.
     * @returns {Promise<object>} A promise that resolves to the voter data for the precinct.
     */
    async fetchPrecinctDemVoterData(county, precinct) {
      if (!(precinct in this.precinctDemVoters)) {
        this.precinctDemVoters[precinct] = fetchJsonData(
          `reports/sos/${county}/ByPrecinct/${precinct}-dem-voters.json`
        );
        this.precinctDemVoters[precinct].catch(error => {
          console.error("Error fetching Precinct Dem Voter data:", error);
        });
      }

      return await this.precinctDemVoters[precinct];
    },
    /**
     * Fetches Democratic voter data for a specific precinct.
     * @async
     * @param {string} county - The name of the county.
     * @param {string} precinct - The precinct identifier.
     * @returns {Promise<object>} A promise that resolves to the voter data for the precinct.
     */
    async fetchPrecinctVoterData(county, precinct, party) {
      if (!["dem", "rep", "ind", "all"].includes(party)) {
        throw new Error("Party must be 'dem', 'rep', 'ind', or 'all'.");
      }
      if (!(precinct in this.precinctVoters)) {
        this.precinctVoters[precinct] = {};
      }
      if (!(party in this.precinctVoters[precinct])) {
        this.precinctVoters[precinct][party] = fetchJsonData(
          `reports/sos/${county}/ByPrecinct/${precinct}-${party}-voters.json`
        );
        this.precinctVoters[precinct][party].catch(error => {
          console.error(`Error fetching Precinct ${party} Voter data:`, error);
        });
      }

      return await this.precinctVoters[precinct][party];
    },

    /**
     * Fetches by-address voter data for a specific precinct.
     * @async
     * @param {string} county - The name of the county.
     * @param {string} precinct - The precinct identifier.
     * @returns {Promise<object>} A promise that resolves to the by-address voter data for the precinct.
     */
    async fetchPrecinctByAddressData(county, precinct) {
      if (!(county && precinct)) {
        console.error(
          "County and precinct must be specified to fetch by address data"
        );
        return {};
      }
      if (!(precinct in this.precinctVoters)) {
        this.precinctVoters[precinct] = {};
      }
      if (!("byAddress" in this.precinctVoters[precinct])) {
        const filePath = `reports/sos/${county}/ByPrecinct/${precinct}-by-address-voters.json`;
        this.precinctVoters[precinct]["byAddress"] = fetchJsonData(filePath);
        this.precinctVoters[precinct]["byAddress"].catch(error => {
          console.error("Error fetching Precinct By-Address data:", error);
        });
      }

      const result = await this.precinctVoters[precinct]["byAddress"];
      return result;
    },

    /**
     * Fetches precinct voter statistics data.
     * @async
     * @returns {Promise<object>} A promise that resolves to the voter statistics data.
     */
    async fetchPrecinctVoterStatisticsData() {
      // TODO: Consider refactoring to be consistent with precinct_metric_data
      if (this.precinctVoterStatistics)
        return await this.precinctVoterStatistics;
      this.precinctVoterStatistics = fetchJsonData(
        `reports/sos/${this.county}-voter-stats.json`
      );
      return await this.precinctVoterStatistics;
    },

    /**
     * Returns an array of voter metrics.
     * @returns {Array<object>} An array of voter metric objects.  Each object contains: name, min, max, unit, category, and function properties.
     */
    getVoterMetrics() {
      return [
        {
          name: "Has Captain",
          min: 0,
          max: 1,
          unit: "",
          category: "Democrats",
          function: async p => {
            const precinctCaptains = await this.fetchPrecinctCaptains();

            return precinctCaptains[p] ? 1 : null;
          },
        },
        {
          name: "Party Contacts",
          min: 0,
          max: 25,
          unit: "",
          category: "Democrats",
          function: async p => {
            const precinctContacts = await this.fetchPrecinctContacts();
            if (precinctContacts === null) return null;
            let c = precinctContacts[p];
            if (!c) c = [];
            return Object.keys(c).length;
          },
        },
        {
          name: "Party Members",
          min: 0,
          max: 25,
          unit: "",
          category: "Democrats",
          function: async p => {
            const precinctContacts = await this.fetchPrecinctContacts();
            if (precinctContacts === null) return null;
            let c = precinctContacts[p];
            if (!c) c = [];
            return Object.values(c).filter(x => x.Status == "Member").length;
          },
        },
        {
          name: "Membership Rate",
          min: 0,
          max: 10,
          unit: "%",
          category: "Democrats",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            const precinctContacts = await this.fetchPrecinctContacts();
            if (precinctContacts === null) {
              return null;
            }
            let c = precinctContacts[p];
            if (!c) c = [];
            const vs = precinctVoterStatistics["Party Affil"][p];
            if (vs == null || vs["D"] == null) {
              return -1;
            }
            return (
              (Object.values(c).filter(x => x.Status == "Member").length /
                vs["D"]) *
              100
            );
          },
        },
        {
          name: "Democratic",
          min: 0,
          max: 90,
          category: "party",
          unit: "%",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              const pa = precinctVoterStatistics["Party Affil"][p];
              return (pa["D"] / (pa["D"] + pa["R"])) * 100;
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "Republican",
          min: 0,
          max: 90,
          unit: "%",
          category: "party",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              const pa = precinctVoterStatistics["Party Affil"][p];
              return (pa["R"] / (pa["D"] + pa["R"])) * 100;
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "Unaffiliated",
          min: 40,
          max: 80,
          unit: "%",
          category: "party",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              return (
                (precinctVoterStatistics["Party Affil"][p]["X"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "NDDI",
          min: -100,
          max: 100,
          category: "party",
          unit: "% good",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              const pa = precinctVoterStatistics["Party Affil"][p];
              return ((pa["D"] - pa["R"]) / (pa["D"] + pa["R"])) * 100;
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "<25",
          min: 0,
          max: 30,
          unit: "%",
          category: "age",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              return (
                (precinctVoterStatistics["Voter Age"][p]["18-25"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "26-35",
          min: 0,
          max: 30,
          unit: "%",
          category: "age",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              return (
                (precinctVoterStatistics["Voter Age"][p]["26-35"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "36-45",
          min: 0,
          max: 30,
          unit: "%",
          category: "age",
          function: async p => {
            try {
              const precinctVoterStatistics =
                await this.fetchPrecinctVoterStatisticsData();
              return (
                (precinctVoterStatistics["Voter Age"][p]["36-45"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "46-55",
          min: 0,
          max: 30,
          unit: "%",
          category: "age",
          function: async p => {
            try {
              const precinctVoterStatistics =
                await this.fetchPrecinctVoterStatisticsData();
              return (
                (precinctVoterStatistics["Voter Age"][p]["46-55"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "56-65",
          min: 0,
          max: 30,
          unit: "%",
          category: "age",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              return (
                (precinctVoterStatistics["Voter Age"][p]["56-65"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "66-75",
          min: 0,
          max: 30,
          unit: "%",
          category: "age",
          function: async p => {
            try {
              const precinctVoterStatistics =
                await this.fetchPrecinctVoterStatisticsData();
              return (
                (precinctVoterStatistics["Voter Age"][p]["66-75"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "76+",
          min: 0,
          max: 30,
          unit: "%",
          category: "age",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              return (
                (precinctVoterStatistics["Voter Age"][p]["76+"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "<2 yr Reg.",
          unit: "%",
          min: 0,
          max: 20,
          category: "new voter",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              return (
                (precinctVoterStatistics["Registration Age"][p]["<2 years"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "2-4 yr Reg.",
          min: 0,
          max: 20,
          unit: "%",
          category: "new voter",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              return (
                (precinctVoterStatistics["Registration Age"][p]["2-4 years"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "4-8 yr Reg.",
          min: 0,
          max: 20,
          unit: "%",
          category: "new voter",
          function: async p => {
            const precinctVoterStatistics =
              await this.fetchPrecinctVoterStatisticsData();
            try {
              return (
                (precinctVoterStatistics["Registration Age"][p]["4-8 years"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
      ];
    },
    /**
     * Fetches and creates voter metrics with pre-loaded data.
     * @async
     * @returns {Promise<Array<object>>} A promise that resolves to an array of voter metric objects.
     */
    async fetchVoterMetrics() {
      // Pre-fetch all the data that metrics will need
      const [precinctVoterStatistics, precinctCaptains, precinctContacts] =
        await Promise.all([
          this.fetchPrecinctVoterStatisticsData(),
          this.fetchPrecinctCaptains(),
          this.fetchPrecinctContacts(),
        ]);

      return [
        {
          name: "Has Captain",
          min: 0,
          max: 1,
          unit: "",
          category: "Democrats",
          function: async p => {
            return precinctCaptains[p] ? 1 : null;
          },
        },
        {
          name: "Party Contacts",
          min: 0,
          max: 25,
          unit: "",
          category: "Democrats",
          function: async p => {
            if (precinctContacts === null) return null;
            let c = precinctContacts[p];
            if (!c) c = [];
            return Object.keys(c).length;
          },
        },
        {
          name: "Party Members",
          min: 0,
          max: 25,
          unit: "",
          category: "Democrats",
          function: async p => {
            if (precinctContacts === null) return null;
            let c = precinctContacts[p];
            if (!c) c = [];
            return Object.values(c).filter(x => x.Status == "Member").length;
          },
        },
        {
          name: "Membership Rate",
          min: 0,
          max: 10,
          unit: "%",
          category: "Democrats",
          function: async p => {
            if (precinctContacts === null) return null;
            let c = precinctContacts[p];
            if (!c) c = [];
            const vs = precinctVoterStatistics["Party Affil"][p];
            if (vs == null || vs["D"] == null) return -1;
            return (
              (Object.values(c).filter(x => x.Status == "Member").length /
                vs["D"]) *
              100
            );
          },
        },
        {
          name: "Democratic",
          min: 0,
          max: 90,
          category: "party",
          unit: "%",
          function: async p => {
            try {
              const pa = precinctVoterStatistics["Party Affil"][p];
              return (pa["D"] / (pa["D"] + pa["R"])) * 100;
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "Republican",
          min: 0,
          max: 90,
          unit: "%",
          category: "party",
          function: async p => {
            try {
              const pa = precinctVoterStatistics["Party Affil"][p];
              return (pa["R"] / (pa["D"] + pa["R"])) * 100;
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "Unaffiliated",
          min: 40,
          max: 80,
          unit: "%",
          category: "party",
          function: async p => {
            try {
              return (
                (precinctVoterStatistics["Party Affil"][p]["X"] /
                  precinctVoterStatistics["Totals"][p]) *
                100
              );
            } catch (e) {
              return -1;
            }
          },
        },
        {
          name: "NDDI",
          min: -100,
          max: 100,
          category: "party",
          unit: "% good",
          function: async p => {
            try {
              const pa = precinctVoterStatistics["Party Affil"][p];
              return ((pa["D"] - pa["R"]) / (pa["D"] + pa["R"])) * 100;
            } catch (e) {
              return -1;
            }
          },
        },
        // Age metrics helper function
        ...this._createAgeMetrics(precinctVoterStatistics),
        // Registration age metrics helper function
        ...this._createRegistrationMetrics(precinctVoterStatistics),
      ];
    },

    /**
     * Helper to create age-based metrics
     * @private
     */
    _createAgeMetrics(precinctVoterStatistics) {
      const ageRanges = [
        "18-25",
        "26-35",
        "36-45",
        "46-55",
        "56-65",
        "66-75",
        "76+",
      ];
      const ageLabels = [
        "<25",
        "26-35",
        "36-45",
        "46-55",
        "56-65",
        "66-75",
        "76+",
      ];

      return ageRanges.map((range, index) => ({
        name: ageLabels[index],
        min: 0,
        max: 30,
        unit: "%",
        category: "age",
        function: async p => {
          try {
            return (
              (precinctVoterStatistics["Voter Age"][p][range] /
                precinctVoterStatistics["Totals"][p]) *
              100
            );
          } catch (e) {
            return -1;
          }
        },
      }));
    },

    /**
     * Helper to create registration age metrics
     * @private
     */
    _createRegistrationMetrics(precinctVoterStatistics) {
      const regRanges = ["<2 years", "2-4 years", "4-8 years"];
      const regLabels = ["<2 yr Reg.", "2-4 yr Reg.", "4-8 yr Reg."];

      return regRanges.map((range, index) => ({
        name: regLabels[index],
        min: 0,
        max: 20,
        unit: "%",
        category: "new voter",
        function: async p => {
          try {
            return (
              (precinctVoterStatistics["Registration Age"][p][range] /
                precinctVoterStatistics["Totals"][p]) *
              100
            );
          } catch (e) {
            return -1;
          }
        },
      }));
    },

    getMetrics() {
      return this.metrics;
    },
  },
});

export default useDataStore;
